<!-- Responsibility: Marketplace browsing and publishing.
Scope: Owns registry filtering, preforged offer resolution, and publishing triggers. -->
<marketplace-view class="menu-section active menu-overlay">
  <section class="marketplace-hero">
    <div class="marketplace-copy">
      <h3 class="lobby-section-title">WORLD MARKETPLACE</h3>
      <p class="marketplace-intro">
        { state.lane === 'browse'
          ? 'Browse curated test worlds, granted realities, and invite-only catalog entries.'
          : state.lane === 'publish'
            ? 'Publish one of your reusable saved worlds into the registry.'
            : 'Review your own catalog, adjust lifecycle state, and keep the registry current.' }
      </p>
    </div>

    <nav class="marketplace-lane-tabs" onclick={ handleLaneSwitch }>
      <button type="button" class="lane-tab { state.lane === 'browse' ? 'active' : '' }" data-lane="browse">
        BROWSE <span class="lane-count">{ state.entries.length }</span>
      </button>
      <button type="button" class="lane-tab { state.lane === 'publish' ? 'active' : '' }" data-lane="publish">
        PUBLISH <span class="lane-count">{ state.worlds.length }</span>
      </button>
      <button type="button" class="lane-tab { state.lane === 'creator' ? 'active' : '' }" data-lane="creator">
        CREATOR <span class="lane-count">{ state.creatorEntries.length }</span>
      </button>
    </nav>
  </section>

  <div class="marketplace-shell { state.lane }">
    <section class="marketplace-browse" data-active={ state.lane === 'browse' ? 'true' : 'false' }>
      <section if={ state.preforgedOffers.length || state.loading } class="publish-surface preforge-surface">
        <div class="publish-head">
          <strong>CURATED TEST REALITIES</strong>
        </div>
        <p if={ !state.loading || state.preforgedOffers.length } class="publish-copy">These hand-picked worlds and realities are enabled by your test plan or a direct admin grant.</p>
        <div if={ state.loading && !state.preforgedOffers.length } class="publish-list loading-stack" aria-busy="true">
          <div each={ _ in LOADING_ROWS } class="publish-card preforge-card loading-skeleton" aria-hidden="true">
            <div class="publish-world-copy loading-grid">
              <span class="loading-line long"></span>
              <span class="loading-line short"></span>
            </div>
            <div class="publish-card-flags loading-grid">
              <span class="loading-chip"></span>
              <span class="loading-chip" style="width: 7.25rem;"></span>
            </div>
            <span class="loading-chip" style="width: 8.5rem;"></span>
          </div>
        </div>
        <div if={ state.preforgedOffers.length && !state.loading } class="publish-list">
          <button
            each={ offer in state.preforgedOffers }
            type="button"
            class="publish-card preforge-card"
            onclick={ () => launchPreforgedOffer(offer) }
          >
            <div class="publish-world-copy">
              <b>{ offer.title }</b>
              <small>{ offer.summary || `${offer.kind.toUpperCase()} · ${offer.worldManifestId}` }</small>
            </div>
            <div class="publish-card-flags">
              <span>{ offer.kind.toUpperCase() }</span>
              <span>{ offer.access?.statusLabel || 'TEST ACCESS REQUIRED' }</span>
            </div>
            <span class="action-btn primary">{ offer.access?.ctaLabel || 'TEST ACCESS REQUIRED' }</span>
          </button>
        </div>
      </section>

      <div class="marketplace-filters">
        <label class="market-filter">
          <span>Search</span>
          <input
            ref="query"
            type="text"
            placeholder="Search worlds, tags, creators..."
            value={ state.query }
            oninput={ onQueryInput }
          >
        </label>

        <label class="market-filter compact">
          <span>Price</span>
          <select ref="priceModel" value={ state.priceModel } onchange={ onPriceChange }>
            <option value="all">All</option>
            <option value="free">Free</option>
            <option value="paid">Paid</option>
          </select>
        </label>

        <label class="market-filter compact">
          <span>Sort</span>
          <select ref="sort" value={ state.sort } onchange={ onSortChange }>
            <option value="latest">Latest</option>
            <option value="title">Title</option>
            <option value="price">Price</option>
          </select>
        </label>
      </div>

      <div class="marketplace-tags" if={ state.tags.length } onclick={ handleTagClick }>
        <button type="button" class="market-tag { !state.tag ? 'active' : '' }" data-tag="">ALL</button>
        <button each={ t in state.tags } type="button" class="market-tag { state.tag === t ? 'active' : '' }" data-tag={ t }>{ t.toUpperCase() }</button>
      </div>

      <div class="marketplace-catalog">
        <p if={ state.loading && !state.entries.length } class="lobby-hint">Scanning marketplace registry...</p>
        <p if={ state.error } class="lobby-error">{ state.error }</p>
        <p if={ !state.loading && !state.error && !state.entries.length } class="lobby-hint">No marketplace worlds match the current filters.</p>

        <div if={ state.loading && !state.entries.length } class="loading-stack marketplace-loading-stack" aria-busy="true">
          <button each={ _ in LOADING_ROWS } type="button" class="market-card loading-skeleton" disabled aria-hidden="true">
            <div class="market-card-provenance loading-grid">
              <div class="market-card-provenance-row">
                <span class="market-creator-badge loading-avatar"></span>
                <span class="market-creator-copy loading-grid">
                  <span class="loading-line long"></span>
                  <span class="loading-line short"></span>
                </span>
              </div>
              <span class="market-provenance-pill loading-chip"></span>
            </div>

            <div class="market-card-head loading-grid">
              <div class="market-card-title loading-grid">
                <span class="loading-line long"></span>
                <span class="loading-line short"></span>
              </div>
              <span class="loading-line short market-price-skeleton"></span>
            </div>

            <span class="loading-line full"></span>
            <span class="loading-chip market-cta-skeleton"></span>
          </button>
        </div>

        <button
          each={ entry in state.entries }
          type="button"
          class="market-card"
          onclick={ () => importMarketplaceWorld(entry) }
        >
          <span class="market-card-provenance">
            <span class="market-creator-badge">{ creatorBadge(entry) }</span>
            <span class="market-creator-copy">
              <strong>{ entry.creator.displayName }</strong>
              <span>{ entry.creator.uid === 'sample-system' ? 'Curated system seed' : 'Community publisher' }</span>
            </span>
            <span class="market-provenance-pill">{ provenanceLabel(entry) }</span>
          </span>

          <span class="market-card-head">
            <span class="market-card-title">
              <strong>{ entry.title }</strong>
              <span class="market-safety { entry.nsfw ? 'nsfw' : '' }">{ entry.nsfw ? 'NSFW' : 'SAFE' }</span>
            </span>
            <span class="market-price">
              { entry.pricing.model === 'free' ? 'FREE' : `${entry.pricing.currency} ${(entry.pricing.priceCents / 100).toFixed(2)}` }
            </span>
          </span>

          <span class="market-card-copy">{ entry.intro }</span>
          <span class="market-card-cta">{ entry.commerceAccess?.ctaLabel || 'IMPORT WORLD' }</span>
        </button>
      </div>
    </section>

    <aside class="publish-surface" data-active={ state.lane === 'publish' ? 'true' : 'false' }>
      <div class="publish-head">
        <strong>PUBLISH YOUR REUSABLE WORLDS</strong>
        <button type="button" class="action-btn" onclick={ refreshMarketplace }>REFRESH</button>
      </div>
      <p class="publish-copy">Publishing creates a verified world manifest and a portable package.</p>
      
      <div class="publish-list">
        <p if={ !state.worlds.length } class="lobby-hint">No reusable saved worlds available to publish yet.</p>
        <div each={ world in state.worlds } class="publish-card">
          <span class="publish-world-copy">
            <b>{ world.seedLabel }</b>
            <small>{ world.roomName }</small>
          </span>
          <button
            type="button"
            class="action-btn primary"
            disabled={ state.publishingSaveId === world.saveId }
            onclick={ () => publishMarketplaceWorld(world) }
          >{ state.publishingSaveId === world.saveId ? 'PUBLISHING...' : 'PUBLISH' }</button>
        </div>
      </div>
    </aside>

    <section if={ state.lane === 'creator' } class="creator-surface">
      <div class="creator-head">
        <div class="creator-copy">
          <strong>CREATOR CONSOLE</strong>
          <p>{ state.entitlement.creatorConsoleAccess ? 'Manage your catalog lifecycle and track publishing state.' : 'Creator console access is not currently granted, but your registry entries are still visible here.' }</p>
        </div>
        <div class="creator-stats">
          <span>Slots: { state.entitlement.creatorPublishSlots || 0 }</span>
          <span>Published: { publishedCreatorCount() }</span>
          <span>Draft/Retired: { creatorLifecycleCount('draft') + creatorLifecycleCount('retired') }</span>
        </div>
      </div>

      <div if={ !state.loading || state.creatorAnalytics.summary.catalogCount || state.creatorAnalytics.summary.receiptCount } class="creator-analytics">
        <article class="creator-metric">
          <strong>{ state.creatorAnalytics.summary.catalogCount }</strong>
          <span>Catalog entries</span>
        </article>
        <article class="creator-metric">
          <strong>{ state.creatorAnalytics.summary.listedCount }</strong>
          <span>Live listings</span>
        </article>
        <article class="creator-metric">
          <strong>{ state.creatorAnalytics.summary.paidReceiptCount }</strong>
          <span>Paid sales</span>
        </article>
        <article class="creator-metric">
          <strong>{ formatMoney(state.creatorAnalytics.summary.grossRevenueCents) }</strong>
          <span>Gross revenue</span>
        </article>
      </div>

      <section if={ !state.loading || state.creatorAnalytics.summary.catalogCount || state.creatorAnalytics.summary.receiptCount } class="creator-receipts">
        <div class="creator-receipts-head">
          <strong>RECENT RECEIPTS</strong>
          <span>{ state.creatorAnalytics.summary.receiptCount } active · { state.creatorAnalytics.summary.refundedReceiptCount } refunded</span>
        </div>
        <div if={ state.creatorAnalytics.summary.recentReceipts.length } class="creator-receipt-list">
          <article each={ receipt in state.creatorAnalytics.summary.recentReceipts } class="creator-receipt">
            <div class="creator-receipt-copy">
              <b>{ receipt.title || receipt.artifactId }</b>
              <span>{ receipt.userEmail || receipt.userUid || 'Anonymous buyer' }</span>
            </div>
            <div class="creator-receipt-meta">
              <span>{ receipt.source.replace(/_/g, ' ').toUpperCase() } · { receipt.state.toUpperCase() }</span>
              <span>{ formatMoney(receipt.amountPaidCents, receipt.currency) }</span>
            </div>
          </article>
        </div>
        <p if={ !state.creatorAnalytics.summary.recentReceipts.length } class="lobby-hint">No receipts yet for this catalog.</p>
      </section>

      <div if={ state.loading && !state.creatorEntries.length } class="loading-stack creator-loading" aria-busy="true">
        <article each={ _ in LOADING_ROWS } class="creator-card loading-skeleton" aria-hidden="true">
          <div class="creator-card-top loading-grid">
            <span class="loading-line long"></span>
            <span class="loading-chip"></span>
          </div>
          <div class="creator-card-meta loading-grid">
            <span class="loading-line full"></span>
            <span class="loading-line mid"></span>
          </div>
          <div class="creator-card-controls">
            <span class="loading-chip"></span>
            <span class="loading-chip" style="width: 8rem;"></span>
          </div>
        </article>
      </div>

      <p if={ !state.loading && !state.creatorEntries.length } class="lobby-hint">No catalog entries found for this account yet.</p>

      <div if={ state.creatorEntries.length } class="creator-catalog">
        <article each={ entry in state.creatorEntries } class="creator-card">
          <div class="creator-card-top">
            <div class="creator-card-copy">
              <strong>{ entry.title }</strong>
              <span>{ creatorLifecycleLabel(entry) } · { entry.visibility.toUpperCase() }</span>
            </div>
            <span class="creator-card-badge">{ entry.publishState.toUpperCase() }</span>
          </div>

          <div class="creator-card-meta">
            <p>{ entry.intro }</p>
            <small>{ entry.creator.displayName } · { entry.tags.slice(0, 4).join(' · ') || 'No tags' }</small>
          </div>

          <div class="creator-card-controls">
            <label class="creator-control">
              <span>Lifecycle</span>
              <select value={ state.creatorLifecycleModes[entry.id] || creatorLifecycleValue(entry) } onchange={ onCreatorLifecycleChange } data-world-id={ entry.id }>
                <option value="public">Public</option>
                <option value="unlisted">Unlisted</option>
                <option value="private">Private</option>
                <option value="draft">Draft</option>
                <option value="retired">Retired</option>
              </select>
            </label>

            <button
              type="button"
              class="action-btn primary"
              disabled={ state.creatorSavingId === entry.id }
              onclick={ () => saveCreatorLifecycle(entry) }
            >{ state.creatorSavingId === entry.id ? 'SAVING...' : 'SAVE LIFECYCLE' }</button>
          </div>
        </article>
      </div>
    </section>
  </div>

  <a class="nav-link" onclick={ goHub }>← BACK TO HUB</a>

  <script>
    import * as gameStore from '../../game-store.js'
    import { authStore } from '../stores/auth-store.js'
    import { commerceStore } from '../stores/commerce-store.ts'
    import { navigateState, sysNotify } from '../../app-states.js'
    import { persistenceService } from '../../services/persistence-service.ts'
    import { launchPreforgedReality } from '../../services/preforge-service.ts'
    import { loadCreatorAnalytics } from '@anyquest/shared-core'
    import { 
      listMarketplaceWorlds, 
      listCreatorWorldRegistry,
      marketplaceTags, 
      upsertMarketplaceWorldManifest,
      publishMarketplaceWorld as publishToRegistry,
      marketplaceManifestToImportedWorldData
    } from '../../engine/marketplace.ts'
    import { describeMarketplaceAccess, describePreforgedOfferAccess } from '../../commerce/commerce-contract.ts'

    const LOADING_ROWS = [0, 1, 2, 3]

    function emptyCreatorAnalytics(creatorUid = '') {
      return {
        summary: {
          creatorUid,
          catalogCount: 0,
          listedCount: 0,
          draftCount: 0,
          retiredCount: 0,
          publicCount: 0,
          unlistedCount: 0,
          privateCount: 0,
          receiptCount: 0,
          paidReceiptCount: 0,
          freeReceiptCount: 0,
          includedPlanReceiptCount: 0,
          adminGrantReceiptCount: 0,
          refundedReceiptCount: 0,
          grossRevenueCents: 0,
          worlds: [],
          recentReceipts: [],
        },
      }
    }

    function formatMoney(cents, currency = 'USD') {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format((Number(cents) || 0) / 100)
    }

    export default {
      state: {
        lane: 'browse',
        entries: [],
        tags: [],
        worlds: [],
        creatorEntries: [],
        preforgedOffers: [],
        loading: true,
        error: null,
        entitlement: commerceStore.entitlement,
        creatorSavingId: null,
        creatorAnalytics: emptyCreatorAnalytics(),
        query: '',
        tag: '',
        priceModel: 'all',
        sort: 'latest',
        publishingSaveId: null,
        creatorLifecycleModes: {},
      },

      onMounted() {
        this.refreshMarketplace()
      },

      async refreshMarketplace() {
        const user = authStore.user
        this.update({ loading: true, error: null, entries: [], tags: [], worlds: [], creatorEntries: [], creatorLifecycleModes: {}, preforgedOffers: [], creatorAnalytics: emptyCreatorAnalytics(user?.uid) })
        try {
          const [entries, worlds, creatorAnalytics] = await Promise.all([
            listMarketplaceWorlds({
              query: this.state.query,
              tag: this.state.tag,
              priceModel: this.state.priceModel,
              sort: this.state.sort
            }),
            persistenceService.listWorlds(),
            user?.uid
              ? loadCreatorAnalytics(user.uid).catch(() => emptyCreatorAnalytics(user.uid))
              : Promise.resolve(emptyCreatorAnalytics())
          ])
          const creatorEntries = user?.uid
            ? await listCreatorWorldRegistry(user.uid)
            : []

          const entitlement = commerceStore.entitlement
          const receipts = commerceStore.receipts
          const grants = commerceStore.testerGrants

          this.update({
            entries: entries.map(e => ({ ...e, commerceAccess: describeMarketplaceAccess(e, entitlement, receipts) })),
            tags: marketplaceTags(entries),
            worlds: worlds.map(w => ({ ...w, seedLabel: this.worldSeedLabel(w.seed) })),
            creatorEntries,
            creatorLifecycleModes: Object.fromEntries(creatorEntries.map(entry => [entry.id, this.creatorLifecycleValue(entry)])),
            preforgedOffers: (commerceStore.preforgedOffers || []).map(o => ({ ...o, access: describePreforgedOfferAccess(o, entitlement, grants) })),
            entitlement,
            creatorAnalytics,
            loading: false
          })
        } catch {
          this.update({ loading: false, error: 'Could not load marketplace.' })
        }
      },

      worldSeedLabel(seed) {
        const firstLine = String(seed ?? '').split('\n')[0].trim()
        return firstLine.length > 66 ? `${firstLine.slice(0, 66)}...` : (firstLine || 'UNTITLED')
      },

      async importMarketplaceWorld(entry) {
        if (entry.commerceAccess?.requiresAcquire) {
          sysNotify('Test access required.')
          return
        }
        gameStore.setImportedWorldData(marketplaceManifestToImportedWorldData(entry))
        sysNotify(`Loaded: ${entry.title}`)
        navigateState('vessels')
      },

      async publishMarketplaceWorld(world) {
        const user = authStore.user
        if (!user || this.state.publishingSaveId) return
        this.update({ publishingSaveId: world.saveId })
        try {
          const snapshot = await gameStore.loadWorldSnapshot(world.saveId)
          await publishToRegistry(snapshot, {
            creatorUid: user.uid,
            creatorName: user.displayName || user.email || 'Anonymous',
            sourceSaveId: world.saveId
          })
          sysNotify('Published!')
          this.refreshMarketplace()
        } catch {
          sysNotify('Publish failed.')
        } finally {
          this.update({ publishingSaveId: null })
        }
      },

      creatorLifecycleValue(entry) {
        if (entry.publishState === 'retired') return 'retired'
        if (entry.publishState === 'draft') return 'draft'
        if (entry.visibility === 'private') return 'private'
        if (entry.visibility === 'unlisted') return 'unlisted'
        return 'public'
      },

      creatorLifecycleLabel(entry) {
        switch (this.creatorLifecycleValue(entry)) {
          case 'draft': return 'Draft'
          case 'retired': return 'Retired'
          case 'private': return 'Private'
          case 'unlisted': return 'Unlisted'
          default: return 'Public'
        }
      },

      creatorLifecycleCount(mode) {
        return (this.state.creatorEntries || []).filter(entry => this.creatorLifecycleValue(entry) === mode).length
      },

      publishedCreatorCount() {
        return (this.state.creatorEntries || []).filter(entry => this.creatorLifecycleValue(entry) === 'public' || this.creatorLifecycleValue(entry) === 'unlisted').length
      },

      onCreatorLifecycleChange(e) {
        const worldId = String(e?.currentTarget?.dataset?.worldId ?? '').trim()
        if (!worldId) return
        const mode = String(e?.currentTarget?.value ?? '').trim() || 'public'
        this.update({
          creatorLifecycleModes: {
            ...this.state.creatorLifecycleModes,
            [worldId]: mode,
          },
        })
      },

      async saveCreatorLifecycle(entry) {
        const user = authStore.user
        if (!user || this.state.creatorSavingId) return
        const mode = this.state.creatorLifecycleModes[entry.id] || this.creatorLifecycleValue(entry)
        const next = {
          ...entry,
          publishState: mode === 'draft' ? 'draft' : mode === 'retired' ? 'retired' : 'published',
          visibility: mode === 'private' ? 'private' : mode === 'unlisted' ? 'unlisted' : 'public',
        }
        this.update({ creatorSavingId: entry.id })
        try {
          await upsertMarketplaceWorldManifest(next)
          sysNotify(`Updated: ${entry.title}`)
          await this.refreshMarketplace()
        } catch {
          sysNotify('Could not update catalog lifecycle.')
        } finally {
          this.update({ creatorSavingId: null })
        }
      },

      async launchPreforgedOffer(offer) {
        if (!offer.access?.allowed) {
          sysNotify('Test access required.')
          return
        }
        if (offer.starterCode) {
          try {
            const result = await launchPreforgedReality(offer)
            navigateState('scene', result.saveId)
            return
          } catch (error) {
            sysNotify(`Could not start preforged reality: ${String(error).slice(0, 120)}`)
            return
          }
        }
        const entry = await this.resolveMarketplaceEntry(offer.worldManifestId)
        if (entry) this.importMarketplaceWorld(entry)
      },

      async resolveMarketplaceEntry(id) {
        return (this.state.entries || []).find(e => e.id === id)
      },

      handleLaneSwitch(e) {
        const lane = e.target.closest('button[data-lane]')?.getAttribute('data-lane')
        if (lane) this.update({ lane })
      },

      onQueryInput(e) { this.update({ query: e.target.value }); this.refreshMarketplace() },
      onPriceChange(e) { this.update({ priceModel: e.target.value }); this.refreshMarketplace() },
      onSortChange(e) { this.update({ sort: e.target.value }); this.refreshMarketplace() },
      handleTagClick(e) {
        const tag = e.target.closest('button[data-tag]')?.getAttribute('data-tag')
        if (tag !== undefined) { this.update({ tag }); this.refreshMarketplace() }
      },

      creatorBadge(e) { return (e.creator.displayName || 'A')[0].toUpperCase() },
      provenanceLabel(e) { return e.creator.uid === 'sample-system' ? 'Verified' : 'Community' },
      goHub() { navigateState('hub') }
    }
  </script>

  <style>
    marketplace-view {
      display: flex;
      flex-direction: column;
      gap: var(--space-lg);
      width: 100%;
      max-width: min(100%, 78rem);
    }

    .marketplace-hero {
      display: grid;
      grid-template-columns: 1fr auto;
      gap: var(--space-md);
      align-items: end;
      padding: clamp(var(--space-sm), 2vw, var(--space-md));
      border: 1px solid var(--border);
      border-radius: var(--radius-lg);
      background: linear-gradient(160deg, var(--surface-card-2), var(--bg));
    }

    .marketplace-lane-tabs { display: flex; gap: var(--space-xs); }
    .lane-tab {
      padding: var(--space-xs) var(--space-md);
      border-radius: 2rem;
      border: 1px solid var(--border);
      background: var(--surface-card-3);
      color: var(--text-dim);
      cursor: pointer;
    }
    .lane-tab.active { color: var(--gold); border-color: var(--gold); }

    .marketplace-shell {
      display: grid;
      grid-template-columns: 1.7fr 0.95fr;
      gap: var(--space-md);
    }

    .publish-surface {
      position: sticky;
      top: var(--space-md);
      align-self: start;
    }

    .creator-surface {
      grid-column: 1 / -1;
      display: grid;
      gap: var(--space-md);
      padding: var(--space-md);
      border: 1px solid var(--border);
      border-radius: var(--radius-lg);
      background: linear-gradient(180deg, color-mix(in srgb, var(--surface-card-2) 92%, var(--bg)), color-mix(in srgb, var(--surface-card-3) 86%, #03060b));
      box-shadow: var(--shadow-elev-2);
    }

    .creator-head {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      gap: var(--space-md);
    }

    .creator-copy {
      display: grid;
      gap: 0.25rem;
    }

    .creator-copy strong {
      color: var(--text-prose);
      letter-spacing: 0.08em;
    }

    .creator-copy p {
      margin: 0;
      color: var(--text-muted);
      font-size: var(--type-sm, 0.875rem);
      line-height: 1.5;
    }

    .creator-stats {
      display: grid;
      gap: 0.35rem;
      justify-items: end;
      color: var(--text-muted);
      font-size: var(--type-xs, 0.78rem);
      letter-spacing: 0.05em;
    }

    .creator-analytics {
      display: grid;
      grid-template-columns: repeat(4, minmax(0, 1fr));
      gap: var(--space-sm);
    }

    .creator-metric,
    .creator-receipts {
      border: 1px solid color-mix(in srgb, var(--border) 70%, transparent);
      border-radius: 0.9rem;
      background: color-mix(in srgb, var(--surface-card-2) 92%, var(--bg));
      box-shadow: var(--shadow-elev-1);
    }

    .creator-metric {
      display: grid;
      gap: 0.25rem;
      padding: var(--space-sm) var(--space-md);
    }

    .creator-metric strong {
      color: var(--text-prose);
      font-size: 1.15rem;
      letter-spacing: 0.03em;
    }

    .creator-metric span {
      color: var(--text-muted);
      font-size: var(--type-xs, 0.78rem);
      letter-spacing: 0.05em;
      text-transform: uppercase;
    }

    .creator-receipts {
      display: grid;
      gap: var(--space-sm);
      padding: var(--space-sm) var(--space-md);
    }

    .creator-receipts-head {
      display: flex;
      justify-content: space-between;
      align-items: baseline;
      gap: var(--space-sm);
    }

    .creator-receipts-head strong {
      color: var(--text-prose);
      font-size: 0.8rem;
      letter-spacing: 0.12em;
    }

    .creator-receipts-head span {
      color: var(--text-muted);
      font-size: var(--type-xs, 0.75rem);
    }

    .creator-receipt-list {
      display: grid;
      gap: 0.5rem;
    }

    .creator-receipt {
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: var(--space-sm);
      padding: 0.6rem 0;
      border-top: 1px solid color-mix(in srgb, var(--border) 50%, transparent);
    }

    .creator-receipt:first-child {
      border-top: 0;
      padding-top: 0;
    }

    .creator-receipt-copy {
      display: grid;
      gap: 0.15rem;
    }

    .creator-receipt-copy b {
      color: var(--text-prose);
      font-size: 0.95rem;
    }

    .creator-receipt-copy span,
    .creator-receipt-meta span {
      color: var(--text-muted);
      font-size: var(--type-xs, 0.75rem);
      line-height: 1.35;
    }

    .creator-receipt-meta {
      display: grid;
      gap: 0.15rem;
      justify-items: end;
      text-align: right;
      white-space: nowrap;
    }

    .creator-catalog,
    .creator-loading {
      display: grid;
      gap: var(--space-sm);
    }

    .creator-card {
      display: grid;
      gap: var(--space-sm);
      border: 1px solid color-mix(in srgb, var(--border) 72%, transparent);
      border-radius: 1rem;
      padding: var(--space-md);
      background:
        linear-gradient(180deg, color-mix(in srgb, var(--surface-card-2) 92%, var(--bg)), color-mix(in srgb, var(--surface-card-3) 86%, #03060b));
      box-shadow: var(--shadow-elev-1);
    }

    .creator-card-top {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      gap: var(--space-sm);
    }

    .creator-card-copy {
      display: grid;
      gap: 0.18rem;
    }

    .creator-card-copy strong {
      color: var(--text-prose);
      font-size: 1rem;
      letter-spacing: 0.02em;
    }

    .creator-card-copy span,
    .creator-card-meta p,
    .creator-card-meta small {
      color: var(--text-muted);
      margin: 0;
      line-height: 1.45;
      font-size: var(--type-xs, 0.78rem);
    }

    .creator-card-badge {
      border-radius: var(--radius-pill);
      border: 1px solid color-mix(in srgb, var(--gold) 22%, transparent);
      padding: 0.25rem 0.55rem;
      font-size: var(--type-3xs, 0.625rem);
      letter-spacing: 0.12em;
      color: var(--gold);
      white-space: nowrap;
    }

    .creator-card-controls {
      display: grid;
      gap: var(--space-sm);
    }

    .creator-control {
      display: grid;
      gap: 0.3rem;
    }

    .creator-control span {
      color: var(--text-dim);
      font-size: var(--type-3xs, 0.625rem);
      letter-spacing: 0.12em;
      text-transform: uppercase;
    }

    .creator-control select {
      width: 100%;
      border: 1px solid var(--border);
      border-radius: 0.7rem;
      padding: 0.75rem 0.85rem;
      background: var(--surface);
      color: var(--text-prose);
      font: inherit;
    }

    .market-card, .publish-card {
      padding: var(--space-md);
      border: 1px solid var(--border);
      border-radius: 1rem;
      background: var(--surface-card-2);
      text-align: left;
      cursor: pointer;
    }

    .market-card.loading-skeleton,
    .publish-card.loading-skeleton {
      cursor: progress;
      pointer-events: none;
      display: grid;
      gap: var(--space-sm);
      text-align: left;
      border-color: color-mix(in srgb, var(--border) 86%, #61708a);
      background:
        linear-gradient(180deg, color-mix(in srgb, var(--surface-card-2) 94%, var(--bg)), color-mix(in srgb, var(--surface-card-3) 90%, #03060b)),
        radial-gradient(circle at 18% 18%, color-mix(in srgb, var(--gold) 6%, transparent), transparent 28%);
      box-shadow: var(--shadow-elev-2);
    }

    .market-card:hover { border-color: var(--gold); transform: translateY(-2px); }

    .marketplace-catalog {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: var(--space-sm);
    }

    .market-creator-badge {
      width: 2rem; height: 2rem;
      display: inline-grid; place-items: center;
      border-radius: 50%; background: var(--gold); color: #000;
      font-weight: bold; margin-right: 0.5rem;
    }

    .market-provenance-pill {
      font-size: 0.7rem; padding: 0.2rem 0.5rem; border-radius: 1rem;
      background: rgba(255,255,255,0.1); color: var(--brand-primary);
    }

    .market-price { color: var(--gold); font-weight: bold; }
    .market-safety.nsfw { color: var(--danger); }

    .market-card-provenance-row {
      display: flex;
      align-items: center;
      gap: var(--space-xs);
    }

    .market-price-skeleton {
      justify-self: end;
      width: 4.5rem;
    }

    .market-cta-skeleton {
      width: 8.5rem;
    }

    @media (max-width: 48rem) {
      .marketplace-shell, .marketplace-catalog { grid-template-columns: 1fr; }
      .marketplace-hero { grid-template-columns: 1fr; }
      .creator-analytics { grid-template-columns: 1fr 1fr; }
      .creator-receipt { flex-direction: column; align-items: flex-start; }
      .creator-receipt-meta { justify-items: start; text-align: left; white-space: normal; }
    }
  </style>
</marketplace-view>
